// src/context/SEOContext.jsx
import React, { createContext, useContext, useState, useEffect } from 'react';

const SEOContext = createContext();

export const useSEO = () => {
    const context = useContext(SEOContext);
    if (!context) {
        throw new Error('useSEO must be used within an SEOProvider');
    }
    return context;
};

export const SEOProvider = ({ children }) => {
    const [seoData, setSeoData] = useState({
        title: 'Chema - Member Contribution Platform',
        description: 'Chema is a compassionate platform where members support each other during times of loss through collective contributions.',
        keywords: 'member contributions, death claims, voucher payments, mutual support',
        canonical: '',
        ogImage: '/images/og-image.jpg',
        ogType: 'website',
        twitterCard: 'summary_large_image'
    });

    const [structuredData, setStructuredData] = useState({});

    const updateSEO = (newData) => {
        setSeoData(prev => ({ ...prev, ...newData }));
    };

    const generateOrganizationSchema = () => ({
        '@context': 'https://schema.org',
        '@type': 'Organization',
        'name': 'Chema Platform',
        'description': seoData.description,
        'url': window.location.origin,
        'logo': `${window.location.origin}/images/logo.png`,
        'sameAs': [
            'https://twitter.com/chemaplatform',
            'https://facebook.com/chemaplatform',
            'https://linkedin.com/company/chemaplatform'
        ],
        'contactPoint': {
            '@type': 'ContactPoint',
            'telephone': '+1-555-HELP-CHEMA',
            'contactType': 'customer service',
            'email': 'support@chema.com'
        }
    });

    const generateWebSiteSchema = () => ({
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        'name': 'Chema Platform',
        'url': window.location.origin,
        'potentialAction': {
            '@type': 'SearchAction',
            'target': `${window.location.origin}/search?q={search_term_string}`,
            'query-input': 'required name=search_term_string'
        }
    });

    const generateBreadcrumbSchema = (breadcrumbs) => ({
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        'itemListElement': breadcrumbs.map((breadcrumb, index) => ({
            '@type': 'ListItem',
            'position': index + 1,
            'name': breadcrumb.name,
            'item': breadcrumb.url
        }))
    });

    useEffect(() => {
        // Set default structured data
        setStructuredData({
            organization: generateOrganizationSchema(),
            website: generateWebSiteSchema()
        });
    }, []);

    const value = {
        seoData,
        updateSEO,
        structuredData,
        setStructuredData,
        generateBreadcrumbSchema
    };

    return (
        <SEOContext.Provider value={value}>
            {children}
        </SEOContext.Provider>
    );
};